import { ScrollTrigger } from 'gsap/ScrollTrigger';

// URL of Calendly's external widget script
const CALENDLY_SCRIPT_SRC =
  'https://assets.calendly.com/assets/external/widget.js';

let calendlyScriptPromise = null;

/**
 * Detect if Calendly's script is already present or initialized.
 * This covers:
 * - Script tag added directly in the HTML (eager loading)
 * - Calendly already loaded and available on window
 */
function isCalendlyAlreadyLoaded() {
  if (
    window.Calendly &&
    typeof window.Calendly.initInlineWidgets === 'function'
  ) {
    return true;
  }

  return !!document.querySelector(
    `script[src="${CALENDLY_SCRIPT_SRC}"], script[src*="assets.calendly.com/assets/external/widget.js"]`,
  );
}

/**
 * Lazily inject Calendly's script once.
 * Returns a promise that resolves when the script has loaded.
 */
function loadCalendlyScript() {
  // If already loaded or present in DOM, don't inject again
  if (isCalendlyAlreadyLoaded()) {
    return Promise.resolve();
  }

  if (!calendlyScriptPromise) {
    calendlyScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = CALENDLY_SCRIPT_SRC;
      script.async = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = (err) => {
        calendlyScriptPromise = null; // allow retry if desired
        reject(err);
      };

      document.body.appendChild(script);
    });
  }

  return calendlyScriptPromise;
}

/**
 * Initialize lazy loading for Calendly inline widgets.
 *
 * @param {Object} options
 * @param {string} [options.selector='.calendly-inline-widget'] - Widget selector
 * @param {string} [options.start='top 80%'] - ScrollTrigger start position
 */
export function initAllCalendlyWidgets({
  selector = '.calendly-inline-widget',
  start = 'top bottom+=100%',
} = {}) {
  if (isCalendlyAlreadyLoaded()) {
    return;
  }

  const widgets = document.querySelectorAll(selector);
  if (!widgets.length) return;

  widgets.forEach((el) => {
    // Avoid setting up multiple ScrollTriggers for the same element
    if (el.dataset.calendlyLazyInit === 'true') return;
    el.dataset.calendlyLazyInit = 'true';

    ScrollTrigger.create({
      trigger: el,
      start,
      once: true,
      // markers: true,
      onEnter: (self) => {
        loadCalendlyScript()
          .then(() => {
            // Calendly auto-scans the DOM for `.calendly-inline-widget` once its script runs.
            // Refresh ScrollTrigger in case layout changes when the widget renders.

            ScrollTrigger.refresh();
          })
          .catch((err) => {
            console.error('Failed to load Calendly script:', err);
          })
          .finally(() => {
            self.kill();
          });
      },
    });
  });
}
